import { useEffect, useState } from "react";
import { api, type AuditEntry } from "../lib/api.ts";
import { relativeTime } from "../lib/utils.ts";

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.audit.list({ limit: 200 })
      .then(setEntries)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function parseSummary(json: string): string {
    try {
      const obj = JSON.parse(json) as Record<string, unknown>;
      return Object.entries(obj)
        .filter(([k]) => k !== "article_id")
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(" · ");
    } catch { return json; }
  }

  function eventColor(type: string): string {
    if (type.includes("error"))     return "text-red-600";
    if (type.includes("published")) return "text-green-600";
    if (type.includes("approved"))  return "text-green-600";
    if (type.includes("rejected"))  return "text-red-500";
    if (type.includes("rewrite"))   return "text-blue-600";
    return "text-gray-700";
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-900 mb-5">Audit Log</h1>
      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {entries.length === 0 && (
            <div className="p-6 text-center text-gray-400 text-sm">No events yet</div>
          )}
          {entries.map(entry => (
            <div key={entry.id} className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-mono text-xs font-medium ${eventColor(entry.event_type)}`}>
                    {entry.event_type}
                  </span>
                  <span className="text-xs text-gray-400">{entry.actor}</span>
                  {entry.article_id && (
                    <a href={`/articles/${entry.article_id}`}
                       className="text-xs text-brand-500 hover:underline">
                      article #{entry.article_id}
                    </a>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{parseSummary(entry.summary)}</p>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {relativeTime(entry.created_at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
